(() => {
  document.documentElement.classList.add('ics-no-marketing-popups');

  if (!window.__icsMarketingPopupGuard) {
    const suppressMarketingPopup = () => {
      document
        .querySelectorAll(
          '[data-testid="POPUP"], [data-testid="POPUP_FORM"], [class*="kl-private-reset-css"]'
        )
        .forEach((element) => {
          const popup = element.matches('[data-testid="POPUP"], [data-testid="POPUP_FORM"]')
            ? element.closest('[role="dialog"]') || element
            : element.closest('[role="dialog"]');
          if (!popup) return;
          popup.style.setProperty('display', 'none', 'important');
          popup.setAttribute('aria-hidden', 'true');
        });
      document.documentElement.style.removeProperty('overflow');
      document.body?.style.removeProperty('overflow');
    };

    const observer = new MutationObserver(suppressMarketingPopup);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.__icsMarketingPopupGuard = observer;
    suppressMarketingPopup();
  }

  const detectEmailProvider = (email) => {
    const domain = String(email).split('@').pop()?.toLowerCase() || '';
    if (domain === 'gmail.com' || domain === 'googlemail.com') return 'gmail';
    if (
      domain === 'outlook.com' ||
      domain === 'hotmail.com' ||
      domain === 'live.com' ||
      domain === 'msn.com'
    ) {
      return 'outlook';
    }
    return 'default';
  };

  const buildEmail = (root, form, caseNumber, provider) => {
    const formData = new FormData(form);
    const email = String(formData.get('email') || '').trim();
    const platform = String(formData.get('platform') || '').trim();
    const issue = String(formData.get('issue_type') || '').trim();
    const message = String(formData.get('message') || '').trim();
    const subject = caseNumber
      ? `[${caseNumber}] Order Support - ${issue} - ${platform}`
      : `[Order Support] ${issue} - ${platform}`;
    const bodyLines = [
      'Hello Inkelligent Support,',
      '',
      'I need help with a purchase.',
      '',
      `Reply email: ${email}`,
      `Purchase source: ${platform}`,
      `Issue: ${issue}`,
      '',
      'Details:',
      message,
      '',
      'Please reply to this email with the next step.',
      '',
      'If relevant, I will attach photos before sending.',
    ];
    if (caseNumber) {
      bodyLines.splice(4, 0, `Support reference: ${caseNumber}`);
    }
    const body = bodyLines.join('\n');
    const recipient = (root.dataset.supportEmail || 'supports@inkelligent.com').trim();
    const encodedRecipient = encodeURIComponent(recipient);
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    return {
      caseNumber,
      provider,
      subject,
      body,
      mailto: `mailto:${recipient}?subject=${encodedSubject}&body=${encodedBody}`,
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedRecipient}&su=${encodedSubject}&body=${encodedBody}`,
      outlook: `https://outlook.live.com/mail/0/deeplink/compose?to=${encodedRecipient}&subject=${encodedSubject}&body=${encodedBody}`,
    };
  };

  document.querySelectorAll('[data-customer-care-support]').forEach((root) => {
    const form = root.querySelector('[data-support-email-form]');
    const platformInput = root.querySelector('[data-support-platform-input]');
    const error = root.querySelector('[data-support-error]');
    const copyStatus = root.querySelector('[data-support-copy-status]');
    const emailOptions = root.querySelector('[data-support-email-options]');
    const emailOptionsHeading = root.querySelector('[data-support-email-options-heading]');
    const openStatus = root.querySelector('[data-support-open-status]');
    const gmailLink = root.querySelector('[data-open-gmail]');
    const outlookLink = root.querySelector('[data-open-outlook]');
    const defaultEmailLink = root.querySelector('[data-open-default-email]');
    const submitButton = root.querySelector('.ics__submit');
    const submitLabel = root.querySelector('[data-support-submit-label]');
    const previewMode = root.dataset.previewMode === 'true';
    let preparedEmail = null;

    root.querySelectorAll('[data-support-platform]').forEach((button) => {
      button.addEventListener('click', () => {
        root.querySelectorAll('[data-support-platform]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        if (platformInput) platformInput.value = button.dataset.supportPlatform || '';
      });
    });

    form?.addEventListener('input', () => {
      if (error) error.hidden = true;
      if (emailOptions) emailOptions.hidden = true;
      preparedEmail = null;
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (error) error.hidden = true;
      if (!form.reportValidity()) return;

      const formData = new FormData(form);
      const email = String(formData.get('email') || '').trim();
      const messageValue = String(formData.get('message') || '').trim();
      const messageField = form.elements.namedItem('message');
      if (messageValue.length < 5) {
        if (error) {
          error.textContent = 'Please add a little more detail (at least 5 characters).';
          error.hidden = false;
          error.focus();
        }
        messageField?.focus();
        return;
      }
      const provider = detectEmailProvider(email);
      const pendingWindow =
        provider === 'gmail' || provider === 'outlook'
          ? window.open('about:blank', '_blank')
          : null;

      if (submitButton) submitButton.disabled = true;
      if (submitLabel) {
        submitLabel.textContent = previewMode
          ? 'Preparing your email…'
          : 'Saving your request…';
      }

      try {
        let result;
        if (previewMode) {
          result = {
            caseNumber: null,
            emailProvider: provider,
          };
        } else {
          const apiPath =
            root.dataset.supportApi || '/apps/customer-care/public/v1/support-requests';
          const response = await fetch(apiPath, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              platform: formData.get('platform'),
              issueType: formData.get('issue_type'),
              message: formData.get('message'),
              website: formData.get('website'),
            }),
          });
          result = await response.json().catch(() => ({}));
          if (!response.ok || !result.caseNumber) {
            const detail = Array.isArray(result.detail)
              ? result.detail.map((item) => item.msg).filter(Boolean).join(' ')
              : result.detail;
            const requestError = new Error(
              result.error || result.message || detail ||
                'We could not save this request. Please try again.'
            );
            requestError.isValidation = response.status === 400 || response.status === 422;
            throw requestError;
          }
        }

        preparedEmail = buildEmail(
          root,
          form,
          result.caseNumber,
          result.emailProvider || provider
        );
        if (gmailLink) gmailLink.href = preparedEmail.gmail;
        if (outlookLink) outlookLink.href = preparedEmail.outlook;
        if (defaultEmailLink) defaultEmailLink.href = preparedEmail.mailto;
        if (emailOptionsHeading) {
          emailOptionsHeading.textContent = previewMode
            ? 'Preview ready — not saved'
            : `Saved as ${preparedEmail.caseNumber}`;
        }
        if (emailOptions) emailOptions.hidden = false;

        if (preparedEmail.provider === 'gmail' && pendingWindow) {
          pendingWindow.location.href = preparedEmail.gmail;
          if (openStatus) openStatus.textContent = 'Gmail has been opened in a new tab.';
        } else if (preparedEmail.provider === 'outlook' && pendingWindow) {
          pendingWindow.location.href = preparedEmail.outlook;
          if (openStatus) openStatus.textContent = 'Outlook has been opened in a new tab.';
        } else if (preparedEmail.provider === 'default') {
          window.location.href = preparedEmail.mailto;
          if (openStatus) {
            openStatus.textContent =
              'We tried to open your default email app. Use an option below if it did not open.';
          }
        } else {
          pendingWindow?.close();
          if (openStatus) {
            openStatus.textContent =
              'Your browser blocked the new tab. Choose an email service below.';
          }
        }
        emailOptionsHeading?.focus();
      } catch (requestError) {
        if (requestError?.isValidation) {
          pendingWindow?.close();
          if (error) {
            error.textContent = requestError.message;
            error.hidden = false;
            error.focus();
          }
          return;
        }
        preparedEmail = buildEmail(root, form, null, provider);
        if (gmailLink) gmailLink.href = preparedEmail.gmail;
        if (outlookLink) outlookLink.href = preparedEmail.outlook;
        if (defaultEmailLink) defaultEmailLink.href = preparedEmail.mailto;
        if (emailOptionsHeading) {
          emailOptionsHeading.textContent = 'Email ready — backup not saved';
        }
        if (emailOptions) emailOptions.hidden = false;

        if (preparedEmail.provider === 'gmail' && pendingWindow) {
          pendingWindow.location.href = preparedEmail.gmail;
          if (openStatus) {
            openStatus.textContent =
              'Gmail has been opened. Send the email so our team receives your request.';
          }
        } else if (preparedEmail.provider === 'outlook' && pendingWindow) {
          pendingWindow.location.href = preparedEmail.outlook;
          if (openStatus) {
            openStatus.textContent =
              'Outlook has been opened. Send the email so our team receives your request.';
          }
        } else if (preparedEmail.provider === 'default') {
          window.location.href = preparedEmail.mailto;
          if (openStatus) {
            openStatus.textContent =
              'We tried to open your default email app. Send the email so our team receives your request.';
          }
        } else {
          pendingWindow?.close();
          if (openStatus) {
            openStatus.textContent =
              'Your browser blocked the new tab; choose an email service below and send the prepared request.';
          }
        }

        if (error) {
          error.textContent =
            'We couldn’t save a backup copy, but your prepared email is ready. Send it so our team receives your request.';
          error.hidden = false;
        }
        emailOptionsHeading?.focus();
      } finally {
        if (submitButton) submitButton.disabled = false;
        if (submitLabel) {
          submitLabel.textContent = previewMode
            ? 'Open my email'
            : 'Save & open my email';
        }
      }
    });

    root.querySelector('[data-copy-support-email]')?.addEventListener('click', async () => {
      if (!preparedEmail) return;
      const value = `To: ${root.dataset.supportEmail || 'supports@inkelligent.com'}\nSubject: ${preparedEmail.subject}\n\n${preparedEmail.body}`;
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        const temporaryInput = document.createElement('textarea');
        temporaryInput.value = value;
        temporaryInput.setAttribute('readonly', '');
        temporaryInput.style.position = 'fixed';
        temporaryInput.style.opacity = '0';
        document.body.appendChild(temporaryInput);
        temporaryInput.select();
        document.execCommand('copy');
        temporaryInput.remove();
      }
      if (copyStatus) {
        copyStatus.textContent =
          'Email details copied. Paste them into any email service.';
      }
    });
  });
})();
